import { memo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, Skeleton } from '@aimsos/ui';

import NoteCommentItem from './NoteCommentItem.jsx';

/**
 * The thread of a note, oldest at the top and the newest at the bottom beside
 * the input (CT-5528). It only RENDERS: paging and posting live in
 * `useNoteComments`.
 *
 * When there are more comments than the page holds, a "load earlier" button sits
 * at the top of the thread and says how many remain, so a long thread tells the
 * reader there is more above instead of looking complete.
 */
const NoteComments = memo(function NoteComments({
  comments,
  total,
  hasMore,
  loading,
  loadingMore,
  error,
  onLoadEarlier,
}) {
  const { t } = useTranslation('unified-profiles');

  if (loading) {
    return (
      <div data-testid="note-comments-loading" className="flex flex-col gap-3" aria-busy>
        <Skeleton width="60%" height="14px" borderRadius="4px" />
        <Skeleton width="85%" height="14px" borderRadius="4px" />
      </div>
    );
  }

  if (error) {
    return (
      <p role="alert" className="text-sm text-content-muted">
        {t('notes.comments.error')}
      </p>
    );
  }

  const remaining = Math.max(0, total - comments.length);

  return (
    <div className="flex flex-col gap-3" data-testid="note-comments">
      {hasMore ? (
        <div className="flex flex-col items-start gap-1" data-testid="note-comments-more">
          <Button variant="ghost" size="sm" onClick={onLoadEarlier} disabled={loadingMore}>
            {t('notes.comments.loadEarlier')}
          </Button>
          <span className="text-xs text-content-muted">
            {t('notes.comments.moreHint', { count: remaining })}
          </span>
        </div>
      ) : null}

      {comments.length === 0 ? (
        <p data-testid="note-comments-empty" className="text-sm text-content-muted">
          {t('notes.comments.empty')}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-4 p-0">
          {comments.map((comment) => (
            <NoteCommentItem key={comment.id} comment={comment} />
          ))}
        </ul>
      )}
    </div>
  );
});

export default NoteComments;
